import {useContext, useLayoutEffect, useRef, useState} from 'react';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Tooltip from '@mui/material/Tooltip';
import {alpha, Theme} from '@mui/material/styles';
import {keyframes, SystemStyleObject} from '@mui/system';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import {useTranslation} from 'react-i18next';
import {NodeType} from 'crqlar-react-common/dist/models/automation/automation';
import BoldNumbers from './BoldNumbers';
import {FlowPreviewContext} from './FlowPreviewContext';
import {hasPeopleBehind, previewCount} from './flowPreviewModel';
import {countMeaning} from './previewText';

const fadeIn = keyframes`from { opacity: 0; }`;

/** The button's height around its text (px): its 4px vertical padding and 1px border. Keep in sync with its sx. */
const BUTTON_CHROME = 10;

/** Space between the button and the layer's edges (px), top and bottom together. */
const BUTTON_MARGIN = 4;

/** Covers the summary and reaches into the card's bottom padding, so a one-line summary fits a button; never grows the card. */
const LAYER_SX: SystemStyleObject<Theme> = {
    position: 'absolute',
    top: '-4px',
    bottom: '-12px',
    left: '-6px',
    right: '-6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    backgroundColor: 'background.paper',
    animation: `${fadeIn} 160ms ease`,
    '@media (prefers-reduced-motion: reduce)': {animation: 'none'},
};

/**
 * A card's preview number, laid over its summary; absolute and opaque, so nothing on the canvas moves.
 * People behind it: a button that opens them. Nobody: plain text. Nothing to count: an empty layer, never a
 * stand-in sentence. No spinners on the canvas; a refresh only dims the text.
 */
const PreviewOverlay = ({nodeId, type}: {nodeId: string; type: NodeType}) => {
    const cards = useContext(FlowPreviewContext);
    const {t} = useTranslation();

    // Line clamp from layout sizes (clientHeight), so the canvas zoom does not count.
    const layerRef = useRef<HTMLDivElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const [lines, setLines] = useState(1);
    const [cut, setCut] = useState(false);
    useLayoutEffect(() => {
        const layer = layerRef.current;
        const text = textRef.current;
        if (!layer || !text) return;
        const lineHeight = parseFloat(getComputedStyle(text).lineHeight);
        if (!lineHeight) return;
        const chrome = text.dataset.button ? BUTTON_CHROME + BUTTON_MARGIN : BUTTON_MARGIN;
        const fit = Math.max(1, Math.floor((layer.clientHeight - chrome) / lineHeight + 0.01));
        setLines(current => current === fit ? current : fit);
        const overflowing = text.scrollHeight > text.clientHeight + 1;
        setCut(current => current === overflowing ? current : overflowing);
    });

    // A still "…" until the first answer: a pulsing card reads as a running automation (AUTOMATIONS_UI.md, Decisions).
    if (!cards?.preview) {
        return cards?.loading ? (
            <Box aria-hidden sx={LAYER_SX}>
                <Box component={'span'} sx={{typography: 'body2', color: 'text.secondary'}}>…</Box>
            </Box>
        ) : null;
    }
    const {preview, refreshing, onOpen} = cards;

    const count = previewCount(nodeId, type, preview);
    if (!count) return null;
    if (count.kind === 'notReached' || count.kind === 'notPreviewed') return <Box aria-hidden sx={LAYER_SX}/>;

    const meaning = countMeaning(nodeId, type, count, preview, t);
    const failed = count.kind === 'failed';
    const broken = failed || (count.kind === 'count' && count.failed);
    const clickable = hasPeopleBehind(preview, nodeId);

    const text = (
        <Box
            ref={textRef}
            component={'span'}
            data-button={clickable ? 'true' : undefined}
            sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: lines,
                overflow: 'hidden',
                minWidth: 0,
                typography: 'body2',
                lineHeight: 1.4,
                textAlign: 'center',
                overflowWrap: 'anywhere',
                color: failed ? 'error.main' : clickable ? 'text.primary' : 'text.secondary',
                // Only the text dims: the layer itself stays opaque.
                opacity: refreshing ? 0.5 : 1,
                transition: 'opacity 160ms ease',
            }}
        >
            {broken && (
                <ErrorOutlineRoundedIcon
                    color={'error'}
                    sx={{fontSize: '1.2em', mr: 0.5, verticalAlign: '-0.25em'}}
                />
            )}
            <BoldNumbers text={meaning}/>
        </Box>
    );

    return (
        <Box ref={layerRef} sx={LAYER_SX}>
            {clickable ? (
                <Tooltip title={cut ? meaning : ''} placement={'bottom'}>
                    <ButtonBase
                        className={'nodrag nopan'}
                        aria-label={meaning}
                        onClick={event => {
                            event.stopPropagation();
                            onOpen(nodeId);
                        }}
                        onDoubleClick={event => event.stopPropagation()}
                        sx={{
                            maxWidth: 'calc(100% - 8px)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.25,
                            pl: 1.25,
                            pr: 0.5,
                            py: '4px',
                            border: '1px solid',
                            borderColor: theme => failed ? alpha(theme.palette.error.main, 0.5) : theme.palette.divider,
                            borderRadius: 1,
                            transition: 'background-color 160ms ease, border-color 160ms ease',
                            '&:hover': {
                                backgroundColor: 'action.hover',
                                borderColor: failed ? 'error.main' : 'text.secondary',
                            },
                        }}
                    >
                        {text}
                        <ChevronRightRoundedIcon sx={{fontSize: 18, flexShrink: 0, color: failed ? 'error.main' : 'text.secondary'}}/>
                    </ButtonBase>
                </Tooltip>
            ) : (
                <Tooltip title={cut ? meaning : ''} placement={'bottom'}>
                    <Box sx={{px: 1, minWidth: 0}}>{text}</Box>
                </Tooltip>
            )}
        </Box>
    );
};

export default PreviewOverlay;
